import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { ApiError, createApiClient } from "../api/client";
import type { DeviceStatus } from "../api/types";
import { PairingScreen } from "../features/pairing/PairingScreen";
import { LicenceNotice, LicenceScreen } from "../features/subscription/LicencePayment";
import { TermsScreen } from "../features/terms/TermsScreen";
import { t } from "../i18n";
import { Logo } from "../ui/Logo";
import { ToastProvider } from "../ui/toasts";
import { deviceToken } from "./device-token";
import { Opening } from "./Opening";
import { checkDevice, tillCredentials, type DeviceCheck } from "./session";
import { applyStoredSize } from "./size";
import { applyStoredTheme } from "./theme";
import { TillShell } from "./TillShell";

/** Where the till is: from a fresh browser to an open till. */
type Stage =
  | { name: "starting" }
  | { name: "unreachable"; message: string }
  | { name: "pairing" }
  | { name: "blocked"; message: string }
  | { name: "licence"; status: DeviceStatus }
  | { name: "terms"; status: DeviceStatus }
  | { name: "ready"; status: DeviceStatus };

export function App() {
  // also without a token: the pairing this browser kept is asked first (checkDevice), so the pairing
  // screen never flashes by
  const [stage, setStage] = useState<Stage>({ name: "starting" });
  const [api] = useState(() => createApiClient(tillCredentials));

  // "Wit" or "Zwart", and "S" or "M", before the first screen is drawn
  useLayoutEffect(() => {
    applyStoredTheme();
    applyStoredSize();
  }, []);

  const show = useCallback((check: DeviceCheck) => {
    setStage(
      check.name === "blocked"
        ? { name: "blocked", message: t.errors.shopInactive }
        : check.name === "app"
          ? { name: "blocked", message: t.errors.isApp(`${window.location.origin}/app`) }
          : check,
    );
  }, []);

  /** Asks the server again where this device stands (after pairing, terms, a paused shop). */
  const start = useCallback(() => {
    void checkDevice(api).then(show);
  }, [api, show]);

  useEffect(() => {
    void checkDevice(api).then(show);
  }, [api, show]);

  /** Errors that change what the till can do move it to the right screen. Returns true if handled. */
  const handleFatal = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof ApiError)) return false;
      if (error.code === "device_not_paired") {
        deviceToken.clear();
        setStage({ name: "pairing" });
        return true;
      }
      if (error.status === 423 || error.code === "shop_inactive" || error.code === "licence_expired") {
        start();
        return true;
      }
      return false;
    },
    [start],
  );

  let screen;
  switch (stage.name) {
    case "starting":
      screen = <Splash />;
      break;
    case "unreachable":
      screen = <Splash message={stage.message} onRetry={start} />;
      break;
    case "blocked":
      screen = <Splash message={stage.message} onRetry={start} />;
      break;
    case "pairing":
      screen = (
        <PairingScreen
          api={api}
          onPaired={(result) => {
            deviceToken.save(result.deviceToken);
            start();
          }}
        />
      );
      break;
    case "licence":
      screen = (
        <LicenceScreen licence={stage.status.licence!} shop={stage.status.shop.name} api={api} onRetry={start} />
      );
      break;
    case "terms":
      screen = (
        <TermsScreen
          api={api}
          status={stage.status}
          onAccepted={() => {
            setStage({
              name: "ready",
              status: { ...stage.status, terms: { ...stage.status.terms, accepted: true, need: null } },
            });
          }}
        />
      );
      break;
    case "ready":
      screen = (
        <>
          <TillShell
            api={api}
            status={stage.status}
            deviceToken={deviceToken.read() ?? ""}
            handleFatal={handleFatal}
            restart={start}
          />
          {stage.status.licence?.payment && <LicenceNotice licence={stage.status.licence} api={api} />}
        </>
      );
      break;
  }

  return (
    <ToastProvider>
      <Opening>{screen}</Opening>
    </ToastProvider>
  );
}

function Splash({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  return (
    <main className="center-screen">
      <div className="card-panel">
        <Logo />
        {message !== undefined && (
          <p className="error-text" role="alert">
            {message}
          </p>
        )}
        {onRetry && (
          <button type="button" className="btn primary" onClick={onRetry}>
            {t.errors.retryNow}
          </button>
        )}
      </div>
    </main>
  );
}
